// Generic CRUD-клиент для owner-scoped публичных каталогов (casing/mud/field/…).
// Единый дом для fetch-boilerplate: list/create/update/remove + ensureOk +
// InUseError. Бэкенд-зеркало — `ssm_public/src/owned_crud.rs` (тот же контракт:
// owner-scope, 404 на чужой/нет, 409 на FK RESTRICT). Конкретный клиент только
// параметризует DTO/Create/Update-типы (ts-rs @webtypes), базовый путь и текст
// сообщения про «используется».

import type { ListFilter } from '@webtypes/ListFilter'
import type { ListPage } from '@webtypes/ListPage'
import type { ListQuery } from '@webtypes/ListQuery'

export async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

// Бросается при DELETE → 409: запись используется по FK ON DELETE RESTRICT.
// `message` задаёт конкретный клиент (своя формулировка про casing/mud/…), а
// `instanceof InUseError` на странице ловит этот случай отдельно от прочих ошибок.
export class InUseError extends Error {}

const JSON_HEADERS = { 'content-type': 'application/json' }

export type CrudClient<Dto, Create, Update> = {
  list:   (query?: ListRequest) => Promise<ListPage<Dto>>
  create: (body: Create) => Promise<Dto>
  update: (id: number, body: Update) => Promise<Dto>
  remove: (id: number) => Promise<void>
}

// В Rust все query-поля nullable, потому что сервер задаёт дефолты. На стороне
// вызова это именно частичный запрос: отсутствующие ключи не кладём в URL.
export type ListRequest = Partial<Omit<ListQuery, 'filters'>> & {
  filters?: ListFilter[]
}

function listUrl(base: string, query: ListRequest): string {
  const params = new URLSearchParams()
  if (query.limit != null) params.set('limit', String(query.limit))
  if (query.offset != null) params.set('offset', String(query.offset))
  if (query.sort_field != null) params.set('sort_field', String(query.sort_field))
  if (query.sort_dir != null) params.set('sort_dir', query.sort_dir)
  if (query.filters != null && query.filters.length > 0) {
    params.set('filters', JSON.stringify(query.filters))
  }
  if (query.q != null && query.q.trim().length > 0) params.set('q', query.q)
  const encoded = params.toString()
  return encoded.length === 0 ? base : `${base}?${encoded}`
}

export function crudClient<Dto, Create, Update>(
  base: string,
  inUseMessage: string,
): CrudClient<Dto, Create, Update> {
  return {
    async list(query: ListRequest = {}): Promise<ListPage<Dto>> {
      const resp = await fetch(listUrl(base, query), { credentials: 'same-origin' })
      await ensureOk(resp)
      return resp.json()
    },
    async create(body: Create): Promise<Dto> {
      const resp = await fetch(base, {
        method:      'POST',
        credentials: 'same-origin',
        headers:     JSON_HEADERS,
        body:        JSON.stringify(body),
      })
      await ensureOk(resp)
      return resp.json()
    },
    async update(id: number, body: Update): Promise<Dto> {
      const resp = await fetch(`${base}/${id}`, {
        method:      'PATCH',
        credentials: 'same-origin',
        headers:     JSON_HEADERS,
        body:        JSON.stringify(body),
      })
      await ensureOk(resp)
      return resp.json()
    },
    async remove(id: number): Promise<void> {
      const resp = await fetch(`${base}/${id}`, {
        method:      'DELETE',
        credentials: 'same-origin',
      })
      if (resp.status === 409) throw new InUseError(inUseMessage)
      await ensureOk(resp)
    },
  }
}
